// File: src/WelcomeBackPage.js
// OOOSH Driver Verification - Welcome Back page for returning drivers
// Shows when all documents are currently valid
// Gives drivers the option to update details (new licence, new address)
// SESSION 97: New component
// Oct 2026: restyled for the hire form redesign (StepShell, Sign part). The
//           update logic and the onContinue / onDetailsChanged contracts are unchanged.

import React, { useState, useCallback } from 'react';
import {
  StepShell, Card, PrimaryButton, CheckRow, Note, Why, PHASE, firstNameOf
} from './ui/HireFormUI';

const WelcomeBackPage = ({
  email,
  driverName,
  onContinue,        // Called when driver says "everything's the same" → go to signature
  onDetailsChanged,  // Called after dates expired → triggers router recalculation
  jobId              // Optional; falls back to ?job= in the URL
}) => {
  const [showUpdateOptions, setShowUpdateOptions] = useState(false);
  const [selectedChanges, setSelectedChanges] = useState({
    newLicence: false,
    newAddress: false
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState(null);

  // Toggle a change option
  const toggleChange = useCallback((key) => {
    setSelectedChanges(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  }, []);

  // Check if any option is selected
  const hasSelection = selectedChanges.newLicence || selectedChanges.newAddress;

  // Handle the update - expire relevant dates in Monday.com
  const handleUpdate = useCallback(async () => {
    if (!hasSelection) return;

    setIsUpdating(true);
    setError(null);

    try {
      // Build the updates object - set relevant dates to yesterday to force re-verification
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const expiredDate = yesterday.toISOString().split('T')[0];

      const updates = {};

      if (selectedChanges.newLicence) {
        // New licence → need to re-verify identity (Idenfy) + new DVLA check
        // Idenfy re-verification will capture the new licence details/ending
        updates.licenseNextCheckDue = expiredDate;
        updates.dvlaValidUntil = expiredDate;
        console.log('🔄 Expiring licence + DVLA validity dates for re-verification');
      }

      if (selectedChanges.newAddress) {
        // New address → need fresh proof of address documents
        updates.poa1ValidUntil = expiredDate;
        updates.poa2ValidUntil = expiredDate;
        console.log('🔄 Expiring POA1 + POA2 validity dates for re-verification');
      }

      // Add a timestamp so we know this was a manual update
      updates.lastUpdated = new Date().toISOString().split('T')[0];

      console.log('📡 Sending date expiry updates to Monday.com:', updates);

      const response = await fetch('/.netlify/functions/monday-integration', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'update-driver-board-a',
          email: email,
          updates: updates
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update your details. Please try again.');
      }

      const result = await response.json();
      console.log('✅ Monday.com updated successfully:', result);

      // Build a summary of what was changed for the callback
      const changedItems = [];
      if (selectedChanges.newLicence) changedItems.push('driving licence');
      if (selectedChanges.newAddress) changedItems.push('address');

      console.log('🔄 Details flagged for re-verification:', changedItems.join(', '));

      // Trigger the router to recalculate the next step
      // The expired dates will cause the router to send them to the right verification steps
      onDetailsChanged({
        changedItems: changedItems,
        newLicence: selectedChanges.newLicence,
        newAddress: selectedChanges.newAddress
      });

    } catch (err) {
      console.error('❌ Failed to update details:', err);
      setError(err.message || 'Something went wrong. Please try again.');
      setIsUpdating(false);
    }
  }, [email, selectedChanges, hasSelection, onDetailsChanged]);

  const displayName = firstNameOf(driverName);
  const jobNumber = jobId || new URLSearchParams(window.location.search).get('job');

  // Collapsing the "Something's changed?" area clears what was ticked, as the
  // old Cancel button did.
  const toggleUpdateOptions = () => {
    if (showUpdateOptions) {
      setSelectedChanges({ newLicence: false, newAddress: false });
      setError(null);
    }
    setShowUpdateOptions(!showUpdateOptions);
  };

  return (
    <StepShell jobId={jobNumber} phase={PHASE.SIGN}>
      <Card>
        <h2 className="hf-h2">Welcome back{displayName ? `, ${displayName}` : ''}</h2>
        <p className="hf-intro">
          Your documents from last time are all still in date, so you can go straight to signing.
        </p>
        <PrimaryButton onClick={onContinue} disabled={isUpdating}>
          Continue to signature
        </PrimaryButton>
      </Card>

      <Card className="hf-card-tight">
        <p className="hf-body hf-strong">Something's changed?</p>
        <button
          type="button"
          className="hf-btn-secondary"
          onClick={toggleUpdateOptions}
          disabled={isUpdating}
          aria-expanded={showUpdateOptions}
        >
          My licence or address has changed
        </button>

        {showUpdateOptions && (
          <>
            <p className="hf-small">Tick everything that applies.</p>
            <div>
              <CheckRow
                checked={selectedChanges.newLicence}
                onToggle={() => !isUpdating && toggleChange('newLicence')}
              >
                I have a new driving licence
              </CheckRow>
              <CheckRow
                checked={selectedChanges.newAddress}
                onToggle={() => !isUpdating && toggleChange('newAddress')}
              >
                I've moved to a new address
              </CheckRow>
            </div>

            {hasSelection && (
              <Why lead="What happens next:">
                We'll ask you to re-verify{' '}
                {selectedChanges.newLicence && selectedChanges.newAddress
                  ? 'your driving licence, DVLA record, and proof of address'
                  : selectedChanges.newLicence
                    ? 'your driving licence and DVLA record'
                    : 'your proof of address documents'
                }. Your other verified details will be kept.
              </Why>
            )}

            {error && <Note tone="error">{error}</Note>}

            <PrimaryButton
              onClick={handleUpdate}
              disabled={!hasSelection}
              busy={isUpdating}
              busyLabel="Updating…"
            >
              Update and re-check
            </PrimaryButton>
          </>
        )}
      </Card>
    </StepShell>
  );
};

export default WelcomeBackPage; 